import { useCallback, useEffect, useState } from "react";
import type { ThemePreference } from "@core/types";
import { safeStorage } from "../lib/storage";

export type Theme = "light" | "dark";

function systemTheme(): Theme {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** The visitor's explicit choice (`?theme=` or the toggle), if any. */
function explicitTheme(): Theme | null {
  const fromUrl = new URLSearchParams(window.location.search).get("theme");
  if (fromUrl === "light" || fromUrl === "dark") return fromUrl;
  const stored = safeStorage.get("theme");
  return stored === "light" || stored === "dark" ? stored : null;
}

/** Visitor's choice → operator default → the device setting. Mirrors the pre-paint script in index.html. */
export function resolveTheme(explicit: Theme | null, operatorDefault: ThemePreference, system: Theme): Theme {
  if (explicit) return explicit;
  return operatorDefault === "light" || operatorDefault === "dark" ? operatorDefault : system;
}

export function useTheme(operatorDefault: ThemePreference = "system") {
  const [explicit, setExplicit] = useState<Theme | null>(explicitTheme);
  const [system, setSystem] = useState<Theme>(systemTheme);
  const theme = resolveTheme(explicit, operatorDefault, system);

  // Follow the device when neither the visitor nor the operator picked a theme.
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const on = () => setSystem(mq.matches ? "dark" : "light");
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme === "dark" ? "#07080C" : "#F4F5F9");
  }, [theme]);

  const toggle = useCallback(() => {
    const next: Theme = theme === "dark" ? "light" : "dark";
    safeStorage.set("theme", next);
    setExplicit(next);
  }, [theme]);

  return { theme, toggle };
}
